import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Privacy-friendly analytics loader.
 * Activates only when the corresponding env variable is set:
 *   - REACT_APP_GA4_ID         → Google Analytics 4 (e.g. G-XXXXXXX)
 *   - REACT_APP_PLAUSIBLE_DOMAIN → Plausible domain (e.g. rotayachting.com.tr)
 *
 * Keep both blank in development to disable tracking.
 */
const GA4_ID = process.env.REACT_APP_GA4_ID;
const PLAUSIBLE_DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN;

const injectScriptOnce = (src, attrs = {}) => {
  if (typeof document === "undefined") return;
  if (document.querySelector(`script[data-rota-analytics="${src}"]`)) return;
  const s = document.createElement("script");
  s.src = src;
  s.async = true;
  s.defer = true;
  s.dataset.rotaAnalytics = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
};

const initGA4 = () => {
  if (!GA4_ID) return;
  injectScriptOnce(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
  if (window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, { anonymize_ip: true, send_page_view: false });
};

const initPlausible = () => {
  if (!PLAUSIBLE_DOMAIN) return;
  injectScriptOnce("https://plausible.io/js/script.js", {
    "data-domain": PLAUSIBLE_DOMAIN,
  });
};

const trackPageview = (path) => {
  if (GA4_ID && window.gtag) {
    window.gtag("event", "page_view", { page_path: path });
  }
  if (PLAUSIBLE_DOMAIN && window.plausible) {
    window.plausible("pageview", { u: window.location.origin + path });
  }
};

const Analytics = () => {
  const location = useLocation();

  useEffect(() => {
    initGA4();
    initPlausible();
  }, []);

  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return null;
};

export default Analytics;
